import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { ApiError } from '../api/client';
import { useEvents } from '../api/events';
import { useCreateUser } from '../api/users';
import { Field } from '../components/Field';

const createSchema = z.object({
  email: z.string().email('Enter a valid email'),
  displayName: z.string().min(1, 'Required'),
  role: z.enum(['owner', 'manager']),
  password: z.string().min(8, 'At least 8 characters'),
  eventIds: z.array(z.string()).optional(),
});

type CreateValues = z.infer<typeof createSchema>;

export function UserCreate() {
  const navigate = useNavigate();
  const eventsQuery = useEvents();
  const createUser = useCreateUser();

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { role: 'manager', eventIds: [] },
  });

  const role = watch('role');

  const onSubmit = handleSubmit(async (values) => {
    try {
      const created = await createUser.mutateAsync({
        email: values.email,
        displayName: values.displayName,
        role: values.role,
        password: values.password,
        // Owners ignore grants; only send eventIds for managers.
        eventIds: values.role === 'manager' ? values.eventIds : undefined,
      });
      navigate(`/users/${created.id}/edit`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'email_taken') {
        setError('email', { message: 'Email already in use' });
      } else {
        setError('root', { message: 'Could not create user.' });
      }
    }
  });

  return (
    <div className="page">
      <div className="page-head">
        <h1>New user</h1>
        <div className="page-actions">
          <Link to="/users" className="btn btn-ghost">
            Cancel
          </Link>
        </div>
      </div>
      <form onSubmit={onSubmit} className="card card-pad">
        <Field label="Display name" error={errors.displayName?.message}>
          <input type="text" autoComplete="off" {...register('displayName')} />
        </Field>
        <Field label="Email" error={errors.email?.message}>
          <input type="email" autoComplete="off" {...register('email')} />
        </Field>
        <Field label="Role">
          <select {...register('role')}>
            <option value="manager">Manager</option>
            <option value="owner">Owner</option>
          </select>
        </Field>
        <Field
          label="Temporary password"
          error={errors.password?.message}
          help="Share with the new user out-of-band; they can change it from My account after first login."
        >
          <input type="text" autoComplete="off" {...register('password')} />
        </Field>

        {role === 'manager' && (
          <Field
            label="Granted events"
            help="Pick the events this manager can access. Leave empty to assign later."
          >
            <div
              className="user-grants"
              style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}
            >
              {eventsQuery.isPending && <div className="micro">Loading events…</div>}
              {eventsQuery.data?.map((ev) => (
                <label
                  key={ev.id}
                  className="user-grant-row"
                  style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                >
                  <input type="checkbox" value={ev.id} {...register('eventIds')} />
                  <span>
                    {ev.name} <span className="micro">({ev.shortCode})</span>
                  </span>
                </label>
              ))}
            </div>
          </Field>
        )}

        {errors.root && <div className="form-error">{errors.root.message}</div>}
        <div className="page-actions">
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </form>
    </div>
  );
}
